import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Autocomplete, Input, PhoneInput, Radio, RadioGroup, TmdxSelect } from '@ds';
import { AddressSearch, ConditionalRender } from '@ui';
import { useAddressForm } from '@/hooks/useAddressForm';

import { preventNonNumericKeys, renderAutocompleteItem, renderSelectItem, getShortTzByIanaName } from '@/utils/helpers';
import { CommonInformationBoard } from './CommonInformationBoard';
import { InformationBoardSection } from './InformationBoardSection';
import { getClinicalCoordinatorType, LOCATION_PREFERENCES, sizes } from '../config';
import { ProfileFormType } from '../types';
import { EmployeeComposed, LocationPreference } from '@/api/apis/resources';
import { EMPLOYEE_ROLE_NAMES, roleNames } from '@/constants/user.constants';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { useCountrySubdivisionInfo } from '@/hooks/useCountrySubdivisionInfo';

type ProfileFormProps = {
  data: EmployeeComposed;
  isReadOnly: boolean;
};

export const ProfileForm = ({ data, isReadOnly }: ProfileFormProps) => {
  const { control, setValue } = useFormContext<ProfileFormType>();
  const { countryCodes, countriesSelectList } = useCountriesData();
  const [locationPreference, country, state, homeLocationTimezone] = useWatch({
    control,
    name: ['locationPreference', 'country', 'state', 'homeLocationTimezone'],
  });
  const {
    addressValue,
    addressList,
    stateSelectList,
    onAddressSelect,
    onStateInputChange,
    onAddressInputChange,
    onCountryInputChange,
  } = useAddressForm();

  const { countryHasStates, stateLabel } = useCountrySubdivisionInfo(country);

  const userRoles = useMemo(() => {
    return data.roles?.map((r) => roleNames[r] ?? '').filter(Boolean) || [];
  }, [data.roles]);

  const isSpecialist = userRoles.includes(EMPLOYEE_ROLE_NAMES['clinical-specialist']);

  const isRetrievalSurgeon = userRoles.includes(EMPLOYEE_ROLE_NAMES['retrieval-surgeon']);

  const isClinicalLead = userRoles.includes(EMPLOYEE_ROLE_NAMES['clinical-lead']);

  const isClinicalCoordinator = userRoles.includes(EMPLOYEE_ROLE_NAMES['clinical-coordinator']);

  const isBaseLocationAndPreferencesShown = isSpecialist || isRetrievalSurgeon || isClinicalLead;

  const isLocationPreferencesBase = locationPreference === LocationPreference.Base;

  const isTimezoneToClear = isBaseLocationAndPreferencesShown && !isLocationPreferencesBase;

  const isUniformMeasurementsShown = isRetrievalSurgeon || isSpecialist;

  const displayTimezone = useMemo(() => {
    const isBaseLocationUsed = isBaseLocationAndPreferencesShown && isLocationPreferencesBase;
    const timezone = isBaseLocationUsed ? data.baseLocation?.location.timezone : homeLocationTimezone;

    return getShortTzByIanaName(timezone || '') || '';
  }, [isBaseLocationAndPreferencesShown, isLocationPreferencesBase, homeLocationTimezone, data.baseLocation]);

  const onAddressSelection = (addressText: string) => {
    onAddressSelect(addressText, isTimezoneToClear || !isBaseLocationAndPreferencesShown);

    const selectedAddress = addressList.find((a) => a.id === addressText);
    const ianaTimezone = selectedAddress?.timeZone?.name || '';

    if (ianaTimezone) {
      setValue('homeLocationTimezone', ianaTimezone);
    }
  };

  const stateNotSelected = countryHasStates && !state;
  const addressDisabled = !country || stateNotSelected;

  return (
    <div className="w-full flex flex-col gap-3">
      <CommonInformationBoard header="Contact Details">
        <div className="h-auto">
          <Controller
            name="phone"
            control={control}
            render={({ field: { ref, value, ...field }, fieldState: { invalid, error, isTouched } }) => (
              <PhoneInput
                countries={countryCodes}
                disabled={isReadOnly}
                selectLabel="Code"
                label="Phone Number"
                placeholder="(000) 0000 00 00"
                selectAriaLabel="Phone code input"
                inputAriaLabel="Phone number input"
                classNames={{ select: 'max-w-35', input: 'max-w-76.75' }}
                value={value}
                isInvalid={invalid && isTouched}
                error={error?.message}
                {...field}
              />
            )}
          />
        </div>
      </CommonInformationBoard>
      <CommonInformationBoard header="Location Information">
        <ConditionalRender show={isBaseLocationAndPreferencesShown}>
          <InformationBoardSection header="Departure details">
            <Controller
              name="baseLocationId"
              control={control}
              render={({ fieldState: { invalid, error } }) => (
                <Input
                  className="max-w-76.75"
                  label="Base Location"
                  aria-label="Base Location"
                  isDisabled
                  isInvalid={invalid}
                  value={data?.baseLocation?.name}
                  errorMessage={error?.message}
                />
              )}
            />
          </InformationBoardSection>
        </ConditionalRender>
        <InformationBoardSection header="Home Location">
          <Controller
            name="country"
            control={control}
            render={({ field: { value, ref, onChange, ...field }, fieldState: { invalid, error } }) => (
              <Autocomplete
                placeholder="Choose an option"
                aria-label="Country"
                label="Country"
                className="max-w-76.75"
                isDisabled={isReadOnly}
                isInvalid={invalid}
                errorMessage={error?.message}
                defaultItems={countriesSelectList}
                selectedKey={value || null}
                onSelectionChange={(key) =>
                  onCountryInputChange(key as string, isTimezoneToClear || !isBaseLocationAndPreferencesShown)
                }
                {...field}
              >
                {renderAutocompleteItem}
              </Autocomplete>
            )}
          />
          <ConditionalRender show={countryHasStates}>
            <Controller
              name="state"
              control={control}
              render={({ field: { value, ref, onChange, ...field }, fieldState: { invalid, error } }) => (
                <Autocomplete
                  placeholder="Choose an option"
                  aria-label={stateLabel}
                  label={stateLabel}
                  className="max-w-76.75"
                  isDisabled={isReadOnly}
                  defaultItems={stateSelectList}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  value={value || ''}
                  selectedKey={value}
                  onSelectionChange={(key) => {
                    if (key !== value) {
                      onStateInputChange(key as string, isTimezoneToClear || !isBaseLocationAndPreferencesShown);
                    }
                  }}
                  {...field}
                >
                  {renderAutocompleteItem}
                </Autocomplete>
              )}
            />
          </ConditionalRender>
          <Controller
            name="addressLine"
            control={control}
            render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <AddressSearch
                aria-label="Search Address"
                addressList={addressList}
                onAddressSelect={onAddressSelection}
                onAddressInputChange={onAddressInputChange}
                value={addressValue}
                onChange={onChange}
                isInvalid={invalid}
                errorMessage={error?.message}
                isDisabled={isReadOnly || addressDisabled}
                wrapperProps={{
                  className: 'w-full max-w-76.75',
                  'aria-label': 'Search Address',
                }}
                inputProps={{
                  label: 'Address',
                }}
                listBoxWrapperProps={{
                  className: 'max-h-29.5',
                }}
                {...field}
              />
            )}
          />
        </InformationBoardSection>
        <InformationBoardSection>
          <Controller
            name="city"
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                className="max-w-76.75"
                aria-label="City"
                label="City"
                type="text"
                isInvalid={invalid}
                isReadOnly
                isDisabled
                errorMessage={error?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="postalCode"
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                aria-label="Postal Code"
                label="Postal Code"
                className="max-w-76.75"
                type="text"
                isInvalid={invalid}
                isReadOnly
                isDisabled
                errorMessage={error?.message}
                {...field}
              />
            )}
          />
          <Input
            label="Timezone"
            aria-label="Timezone"
            className="max-w-76.75"
            type="text"
            isDisabled
            isReadOnly
            value={displayTimezone}
          />
        </InformationBoardSection>
        <InformationBoardSection>
          <Controller
            name="tsaPrecheckNumber"
            control={control}
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <Input
                label="TSA Precheck Number"
                aria-label="TSA Precheck Number"
                className="max-w-76.75"
                type="text"
                isDisabled={isReadOnly}
                isInvalid={invalid}
                errorMessage={error?.message}
                value={value || ''}
                {...field}
              />
            )}
          />
        </InformationBoardSection>
        <ConditionalRender show={isBaseLocationAndPreferencesShown}>
          <InformationBoardSection header="Location Preferences">
            <Controller
              name="locationPreference"
              control={control}
              render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                <RadioGroup
                  className="gap-8"
                  orientation="horizontal"
                  value={value}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  onValueChange={onChange}
                  {...field}
                >
                  {LOCATION_PREFERENCES.map((location) =>
                    location.value === locationPreference ? (
                      <Radio
                        isDisabled
                        className="w-[100px] max-w-none"
                        key={location.value}
                        value={location.value as string}
                        size="lg"
                      >
                        {location.name}
                      </Radio>
                    ) : null,
                  )}
                </RadioGroup>
              )}
            />
          </InformationBoardSection>
        </ConditionalRender>
      </CommonInformationBoard>
      <ConditionalRender show={isUniformMeasurementsShown}>
        <CommonInformationBoard header="Uniform Measurements">
          <div className="flex flex-row items-start justify-start gap-3">
            <Controller
              name="scrubSizeTop"
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <TmdxSelect
                  label="Scrub Size Top"
                  className="max-w-35"
                  isDisabled={isReadOnly}
                  disallowEmptySelection
                  isInvalid={invalid}
                  selectedKeys={value ? [value] : []}
                  items={sizes}
                  errorMessage={error?.message}
                  {...field}
                >
                  {renderSelectItem}
                </TmdxSelect>
              )}
            />
            <Controller
              name="scrubSizeBottom"
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <TmdxSelect
                  label="Scrub Size Bottom"
                  className="max-w-35"
                  isDisabled={isReadOnly}
                  disallowEmptySelection
                  isInvalid={invalid}
                  items={sizes}
                  selectedKeys={value ? [value] : []}
                  errorMessage={error?.message}
                  {...field}
                >
                  {renderSelectItem}
                </TmdxSelect>
              )}
            />
            <Controller
              name="weight"
              control={control}
              render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                <Input
                  className="max-w-35"
                  label="Weight"
                  placeholder="Weight"
                  aria-label="Weight"
                  type="number"
                  value={`${value}`}
                  isDisabled={isReadOnly}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onKeyDown={preventNonNumericKeys}
                  onChange={(e) => onChange(e.target.value ? Number(e.target.value) : 0)}
                  endContent={
                    <div className="pointer-events-none flex items-center">
                      <span className="text-s-r text-base-20">lb</span>
                    </div>
                  }
                  {...field}
                />
              )}
            />
          </div>
        </CommonInformationBoard>
      </ConditionalRender>
      <ConditionalRender show={isSpecialist}>
        <CommonInformationBoard header="Clinical Specialist Specification">
          <div className="flex flex-row items-start justify-start gap-3">
            <Controller
              name="extraCallsComputed"
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <Input
                  label="Extra Calls"
                  className="max-w-35"
                  isDisabled
                  isInvalid={invalid}
                  value={value}
                  errorMessage={error?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="canTravelComputed"
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <Input
                  label="Reallocation"
                  className="max-w-35"
                  isDisabled
                  isInvalid={invalid}
                  value={value}
                  errorMessage={error?.message}
                  {...field}
                />
              )}
            />
          </div>
        </CommonInformationBoard>
      </ConditionalRender>
      <ConditionalRender show={isClinicalCoordinator}>
        <CommonInformationBoard header="Clinical Coordinator Specification">
          <div className="flex flex-row items-start justify-start gap-3">
            <Controller
              name="clinicalCoordinatorType"
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <Input
                  label="TC Level"
                  className="max-w-35"
                  isDisabled
                  isInvalid={invalid}
                  value={getClinicalCoordinatorType(value || '')}
                  errorMessage={error?.message}
                  {...field}
                />
              )}
            />
          </div>
        </CommonInformationBoard>
      </ConditionalRender>
    </div>
  );
};
